import { AlertModal, Box, Button, Checkbox, Delete, DeleteModal, Export, Pagination } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";
import { toast } from "react-toastify";
import { DateRange } from "rsuite/DateRangePicker";
import { endOfDay, startOfDay, subDays } from "rsuite/esm/internals/utils/date";

import dialogStyle from "./audit-log-dialog.scss";
import auditLogTableStyle from "./audit-log-table.scss";
import AuditLogDeletionDialog from "./AuditLogDeletionDialog";
import AuditLogDetailDialog from "./AuditLogDetailDialog";
import AuditLogExportDialog from "./AuditLogExportDialog";
import AuditLogKebabMenu from "./AuditLogKebabMenu";
import { ButtonContainer, ButtonContainerDivider } from "components/button-container/ButtonContainer";
import CustomDatePickerView from "components/common-date-picker/CustomDatePicker";
import { exportFile } from "components/export/common";
import Cross from "components/icons/Cross";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import DownArrow from "components/icons/DownArrow";
import SearchIcon from "components/icons/Search";
import { SuccessIcon } from "components/icons/SuccessIcon";
import UpArrow from "components/icons/UpArrow";
import layoutStyle from "components/layout/layout.scss";
import style from "components/reports/erasure-reports-table.scss";
import searchViewStyle from "components/search/search-view.scss";
import DateCell from "components/table/DateCell";
import Table, { createSortByHeaderSuffix, deriveColumnWidth, SortState } from "components/table/Table";
import { TableFooter } from "components/table/TableFooter";
import TextWithTooltip from "components/table/TextWithTooltip";
import UuidLinkCell from "components/table/UuidLinkCell";
import { AuditLog, AuditLogDeleteResponse, AuditLogExportAllResponse } from "domain/auditLog";
import { AUTH_AUDIT_LOG_DELETE } from "domain/authority";
import { AUDIT_LOG_PAGE_SIZE, auditLogService, extractFilename } from "services/audit-log/AuditLogService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import { resolveColor } from "utils/commonFunctions";
import { toUtcDateString } from "utils/format";
import { getObject, RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const MAX_PAGE_NUMBERS_TO_SHOW = 3;
const SORT_COLUMN = "creationTime";
export const DELETE_CONFIRMATION_TEXT = "DELETE";

interface AuditLogRow extends AuditLog {
    checked?: boolean;
}

interface TableState {
    auditLogs: AuditLogRow[];
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageCount: number;
}

interface FilterState {
    usernamePrefix: string;
    eventType: string;
    from?: string;
    to?: string;
}

export interface Props {
    refreshCount: number;
    onDataLoaded?: (hasData: boolean) => void;
    onExportAllReady?: (handler: () => void) => void;
}

function createSortHeader(text: string, identifier: string, tooltipText?: string): React.FC {
    return (sortState: SortState) => {
        const suffix = createSortByHeaderSuffix(sortState, identifier);
        const theme = useTheme();
        return (
            <TextWithTooltip text={tooltipText} key={identifier}>
                {text}
                <button
                    className={classNames(style.tableSortButton, {
                        [style.sortNone]: suffix === "",
                        [style.sortAscending]: suffix === "ascending",
                        [style.sortDescending]: suffix === "descending",
                    })}
                >
                    <DownArrow
                        className={style.downArrow}
                        color={suffix === "descending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                    />
                    <UpArrow
                        className={style.upArrow}
                        color={suffix === "ascending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                    />
                </button>
            </TextWithTooltip>
        );
    };
}

export default function AuditLogsTable(props: Props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const tableRef = React.useRef<HTMLDivElement>(null);
    const defaultDateRange: DateRange = [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())];

    const [initialLoading, setInitialLoading] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [deselected, setDeselected] = React.useState(false);
    const [sortAscending, setSortAscending] = React.useState(() => {
        const saved = getObject<{ sortBy?: { id: string; desc: boolean }[] }>(RepositoryKey.AUDIT_LOGS_TABLE);
        return saved?.sortBy?.[0]?.desc === false;
    });
    const [tableState, setTableState] = React.useState<TableState>({
        auditLogs: [],
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageCount: 0,
    });
    const [filterState, setFilterState] = React.useState<FilterState>({
        usernamePrefix: "",
        eventType: "",
        from: toUtcDateString(defaultDateRange[0]),
        to: toUtcDateString(defaultDateRange[1]),
    });
    const [usernameInput, setUsernameInput] = React.useState("");
    const [selectedLog, setSelectedLog] = React.useState<AuditLogRow | null>(null);
    const [eventTypes, setEventTypes] = React.useState<string[]>([]);
    const eventTypesFetched = React.useRef(false);
    const [errorModal, setErrorModal] = React.useState({ isOpen: false, title: "" });
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = React.useState(false);
    const [deletionResponse, setDeletionResponse] = React.useState<AuditLogDeleteResponse | null>(null);
    const [exportAllResponse, setExportAllResponse] = React.useState<AuditLogExportAllResponse | null>(null);
    const allLogsRef = React.useRef<AuditLogRow[]>([]);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    React.useEffect(() => {
        return () => abortControllers.forEach((controller) => controller.abort());
    }, []);

    const fetchData = (
        page: number,
        filters: FilterState,
        sortAscending: boolean,
        reinitialized = false,
        abortController?: AbortController
    ) => {
        const controller = abortController ?? new AbortController();
        if (!abortController) {
            abortControllers.push(controller);
        }
        reinitialized ? setInitialLoading(true) : setLoading(true);
        auditLogService
            .fetchAuditLogs(
                {
                    page,
                    size: AUDIT_LOG_PAGE_SIZE,
                    usernamePrefix: filters.usernamePrefix || undefined,
                    eventType: filters.eventType || undefined,
                    startDate: filters.from,
                    endDate: filters.to,
                    sortOrder: sortAscending ? "ASC" : "DESC",
                    includeEventTypes: !eventTypesFetched.current ? true : undefined,
                },
                controller
            )
            .then((result) => {
                const rows = result.logs.map((log) => ({ ...log, checked: false }));
                allLogsRef.current = rows;
                setTableState((prev) => ({
                    ...prev,
                    auditLogs: rows,
                    currentPage: page,
                    totalPages: Math.ceil(result.totalCount / AUDIT_LOG_PAGE_SIZE),
                    totalCount: result.totalCount,
                    pageCount: result.logs.length,
                }));
                props.onDataLoaded?.(result.totalCount > 0);
                if (result.eventTypes && result.eventTypes.length > 0) {
                    setEventTypes(result.eventTypes);
                    eventTypesFetched.current = true;
                }
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setRequestFailureMessage(t("AuditLogs.requestFailed"));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setInitialLoading(false);
                    setLoading(false);
                }
            });
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        fetchData(1, filterState, sortAscending, true, abortController);
        return () => abortController.abort();
    }, [filterState, sortAscending, props.refreshCount]);

    React.useEffect(() => {
        props.onExportAllReady?.(() => handleExportAll(filterState, sortAscending));
    }, [filterState, sortAscending, props.onExportAllReady]);

    const isUuidSelected = (uuid: string) => allLogsRef.current.some((e) => e.uuid === uuid && e.checked);

    const hasAnySelected = () => allLogsRef.current.some((e) => e.checked);

    const hasAllSelected = () => {
        const checked = allLogsRef.current.filter((e) => e.checked);
        return allLogsRef.current.length > 0 && checked.length === allLogsRef.current.length;
    };

    const updateByUuid = (checked: boolean, uuid: string) => {
        setDeselected(false);
        const update = (rows: AuditLogRow[]) => rows.map((e) => (e.uuid === uuid ? { ...e, checked } : e));
        allLogsRef.current = update(allLogsRef.current);
        setTableState((prev) => ({ ...prev, auditLogs: update(prev.auditLogs) }));
    };

    const updateAll = (checked: boolean) => {
        const update = (rows: AuditLogRow[]) => rows.map((e) => ({ ...e, checked }));
        allLogsRef.current = update(allLogsRef.current);
        setTableState((prev) => ({ ...prev, auditLogs: update(prev.auditLogs) }));
    };

    const findSelectedUuids = () => allLogsRef.current.filter((e) => e.checked).map((e) => e.uuid);

    const handleBulkDelete = () => {
        setDeleteConfirmationVisible(false);
        const uuids = findSelectedUuids();
        if (uuids.length === 0) {
            return;
        }
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.DELETE_AUDIT_LOGS });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        auditLogService
            .deleteAuditLogs({ uuids }, abortController)
            .then((response) => {
                if (uuids.length === 1) {
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("AuditLogs.deletion.deleteSuccess", { uuid: response.deletionReportUuid })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                    fetchData(1, filterState, sortAscending, true);
                } else {
                    setDeletionResponse(response);
                }
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("Notification.title.auditLogDeletion.failed") });
            });
    };

    const handleBulkExport = () => {
        const uuids = findSelectedUuids();
        if (uuids.length === 0) {
            return;
        }
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.EXPORT_AUDIT_LOGS });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        auditLogService
            .exportAuditLogs({ uuids }, abortController)
            .then((response) => {
                const filename = extractFilename(response.headers.get("Content-Disposition"));
                return response.blob().then((blob) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    exportFile(url, filename);
                    window.URL.revokeObjectURL(url);
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("AuditLogs.export.exportSuccess", { count: uuids.length })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("Notification.title.auditLogExport.failed") });
            });
    };

    const handleExportAll = (filters: FilterState, ascending: boolean) => {
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.EXPORT_AUDIT_LOGS });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        auditLogService
            .exportAllAuditLogs(
                {
                    usernamePrefix: filters.usernamePrefix || undefined,
                    eventType: filters.eventType || undefined,
                    startDate: filters.from,
                    endDate: filters.to,
                    sortOrder: ascending ? "ASC" : "DESC",
                },
                abortController
            )
            .then((response) => setExportAllResponse(response))
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("Notification.title.auditLogExport.failed") });
            });
    };

    const createFirstColumn = (): Column<AuditLogRow> => ({
        Header: () => (
            <Checkbox
                data-testid={testIds.common.primaryView.table.selectAllCheckbox}
                checked={hasAllSelected()}
                indeterminate={hasAnySelected() && !hasAllSelected()}
                hideLabelSection={true}
                onChange={(e) => updateAll(e.target.checked ? true : !hasAllSelected())}
                name="auditLogAll"
            />
        ),
        accessor: "checked",
        disableSortBy: true,
        minWidth: 72,
        maxWidth: 72,
        Cell: ({
            cell: {
                row: { original: log },
            },
        }: {
            cell: { row: { original: AuditLogRow } };
        }) => (
            <Box gap={"Xxs"} verticalAlignment={"Center"} className={auditLogTableStyle.firstCell}>
                <Checkbox
                    data-testid={testIds.common.primaryView.table.selectRowCheckbox}
                    name="auditLogRow"
                    id={log.uuid}
                    checked={isUuidSelected(log.uuid) && !deselected}
                    value={log.uuid}
                    hideLabelSection={true}
                    onChange={(e) => updateByUuid(e.target.checked, log.uuid)}
                />
                <AuditLogKebabMenu
                    auditLog={log}
                    onDeleted={() => fetchData(1, filterState, sortAscending, true)}
                    onView={() => setSelectedLog(log)}
                />
            </Box>
        ),
    });

    const columns: Column<AuditLogRow>[] = [
        createFirstColumn(),
        {
            Header: () => t("AuditLogs.detail.uuid"),
            accessor: "uuid",
            disableSortBy: true,
            width: deriveColumnWidth(35, tableRef),
            Cell: ({
                cell: {
                    row: { original: log },
                    value,
                },
            }: {
                cell: { row: { original: AuditLogRow }; value: string };
            }) => <UuidLinkCell value={value} handler={() => setSelectedLog(log)} />,
        },
        {
            Header: () => t("AuditLogs.detail.username"),
            accessor: "username",
            disableSortBy: true,
            width: deriveColumnWidth(25, tableRef),
            Cell: ({ cell: { value } }) => value,
        },
        {
            Header: () => t("AuditLogs.detail.eventType"),
            accessor: "eventType",
            disableSortBy: true,
            width: deriveColumnWidth(20, tableRef),
            Cell: ({ cell: { value } }) => value,
        },
        {
            Header: createSortHeader(t("AuditLogs.detail.creationTime"), SORT_COLUMN, t("Common.clickToSort")),
            accessor: SORT_COLUMN,
            width: deriveColumnWidth(15, tableRef),
            Cell: ({ cell: { value } }) => <DateCell value={value} />,
        },
    ];

    const isAnySelected = hasAnySelected();
    const selectedCount = findSelectedUuids().length;
    const selectionInformation = (
        <>
            <div className={style.selectInformation} data-testid={testIds.common.primaryView.table.selectCountLabel}>
                {t("AuditLogs.selected", { total: selectedCount, count: selectedCount })}
            </div>
            <ButtonContainerDivider />
            <Button
                variant="Tertiary"
                onClick={() => {
                    updateAll(false);
                    setDeselected(true);
                }}
                className={classNames(style.linkText, buttonStyle.textButton)}
                data-testid={testIds.common.primaryView.table.deselectButton}
            >
                {t("DeleteReport.information.deselect")}
            </Button>
            <ButtonContainerDivider />
        </>
    );

    return (
        <>
            <div className={classNames(style.tableDetailsGrid, style.gridContentLayout)}>
                <Box verticalAlignment="Center" gap="Md" className={style.tableDetailsDirection}>
                    {isAnySelected && (
                        <div className={style.selected}>
                            <ButtonContainer noSpacingTop={true} gap="MEDIUM" alignment="LEFT">
                                {selectionInformation}
                                <Button
                                    variant="Tertiary"
                                    icon={<Export color={resolveColor("--theme-icon-fill-default")} />}
                                    testId={testIds.common.primaryView.table.actionBar.button}
                                    data-type="EXPORT"
                                    onClick={handleBulkExport}
                                >
                                    {t("Common.export")}
                                </Button>
                                {!hasTenantCookie() &&
                                    userSessionService.userHasAllAuthorities([AUTH_AUDIT_LOG_DELETE]) && (
                                        <Button
                                            variant="TertiaryDelete"
                                            icon={<Delete />}
                                            testId={testIds.common.primaryView.table.actionBar.button}
                                            data-type="DELETE"
                                            onClick={() => setDeleteConfirmationVisible(true)}
                                        >
                                            {t("Common.delete")}
                                        </Button>
                                    )}
                            </ButtonContainer>
                        </div>
                    )}
                </Box>

                <div className={style.filter}>
                    <div className={formStyle.search}>
                        <select
                            className={formStyle.select}
                            value={filterState.eventType}
                            data-testid={testIds.common.primaryView.table.filter.select}
                            data-type="EVENT_TYPE"
                            onChange={(e) => setFilterState((prev) => ({ ...prev, eventType: e.target.value }))}
                        >
                            <option value="">{t("AuditLogs.filters.allEventTypes")}</option>
                            {eventTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div
                        className={classNames(
                            searchViewStyle.inputWithButton,
                            auditLogTableStyle.usernameSearchContainer
                        )}
                    >
                        <div className={searchViewStyle.searchIcon}>
                            <SearchIcon color={theme.iconFillColor} />
                        </div>
                        <input
                            type="text"
                            placeholder={t("AuditLogs.filters.usernamePlaceholder")}
                            value={usernameInput}
                            autoComplete="off"
                            disabled={loading}
                            onChange={(e) => setUsernameInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setFilterState((prev) => ({ ...prev, usernamePrefix: usernameInput }));
                                }
                            }}
                            data-testid={testIds.common.searchInput.itself}
                        />
                        {usernameInput && (
                            <div
                                className={searchViewStyle.clearButton}
                                onClick={() => {
                                    setUsernameInput("");
                                    setFilterState((prev) => ({ ...prev, usernamePrefix: "" }));
                                }}
                                data-testid={testIds.common.searchInput.clearButton}
                            >
                                <Cross color={theme.iconFillColor} />
                            </div>
                        )}
                    </div>
                    <TextWithTooltip text={t("AuditLogs.filters.retentionPolicy")}>
                        <CustomDatePickerView
                            editable={true}
                            category={Category.AUDIT_LOGS}
                            defaultDateRange={defaultDateRange}
                            defaultPlaceholder={true}
                            onUpdate={(from?: string, to?: string) =>
                                setFilterState((prev) => ({
                                    ...prev,
                                    from: from ?? toUtcDateString(defaultDateRange[0]),
                                    to: to ?? toUtcDateString(defaultDateRange[1]),
                                }))
                            }
                        />
                    </TextWithTooltip>
                </div>
            </div>

            <div className={auditLogTableStyle.tableInnerContainer} ref={tableRef}>
                <Table
                    tableIdentity={RepositoryKey.AUDIT_LOGS_TABLE}
                    data={tableState.auditLogs}
                    columns={columns}
                    loaded={!initialLoading && !loading}
                    loading={loading}
                    failureMessage={requestFailureMessage}
                    tooltips={true}
                    scrollTo={0}
                    emptyMessage={
                        !requestFailureMessage && !loading
                            ? filterState.usernamePrefix
                                ? t("Common.searchEmptyStateMessage", { searchQuery: filterState.usernamePrefix })
                                : filterState.eventType
                                  ? t("Common.searchEmptyStateMessage", { searchQuery: filterState.eventType })
                                  : t("AuditLogs.emptyStateMessage")
                            : undefined
                    }
                    testId={testIds.workArea.auditLogs.logs.table.itself}
                    sortCallbacks={{
                        onChange: (_col, ascending) => setSortAscending((prev) => ascending ?? prev),
                        onDisable: () => setSortAscending(false),
                    }}
                />
            </div>

            <TableFooter
                rightColumn={
                    tableState.totalCount > 0 && (
                        <Pagination
                            maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                            totalPages={tableState.totalPages}
                            currentPage={tableState.currentPage}
                            handlePageChange={(page: number) => fetchData(page, filterState, sortAscending)}
                            paginationText={t("AuditLogs.showingLogs", {
                                start: (tableState.currentPage - 1) * AUDIT_LOG_PAGE_SIZE + 1,
                                end: (tableState.currentPage - 1) * AUDIT_LOG_PAGE_SIZE + tableState.pageCount,
                                total: tableState.totalCount,
                            })}
                        />
                    )
                }
            />
            <AuditLogDetailDialog auditLog={selectedLog ?? undefined} onClose={() => setSelectedLog(null)} />
            <AuditLogDeletionDialog
                response={deletionResponse ?? undefined}
                onClose={() => {
                    setDeletionResponse(null);
                    fetchData(1, filterState, sortAscending, true);
                }}
            />
            <AuditLogExportDialog
                response={exportAllResponse ?? undefined}
                totalCount={tableState.totalCount}
                usernamePrefix={filterState.usernamePrefix || undefined}
                eventType={filterState.eventType || undefined}
                dateFilter={filterState.from && filterState.to ? `${filterState.from} - ${filterState.to}` : undefined}
                onClose={() => setExportAllResponse(null)}
            />
            <DeleteModal
                title={t("AuditLogs.deletion.title", { count: selectedCount })}
                isOpen={deleteConfirmationVisible}
                hideModal={() => setDeleteConfirmationVisible(false)}
                onDelete={handleBulkDelete}
                className={dialogStyle.dialog}
                dataType="DELETE_AUDIT_LOGS"
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_TEXT,
                    deleteButtonText: t("Common.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                testIds={{
                    itself: testIds.common.confirmationDialog.itself,
                    cancelButtonId: testIds.common.confirmationDialog.closeButton,
                    deleteButtonId: testIds.common.confirmationDialog.confirmButton,
                }}
            >
                {t("AuditLogs.deletion.confirmationMessageMultiple", { count: selectedCount })}
            </DeleteModal>
            <AlertModal
                title={errorModal.title}
                variant="Warning"
                isOpen={errorModal.isOpen}
                hideModal={() => setErrorModal({ isOpen: false, title: "" })}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setErrorModal({ isOpen: false, title: "" })}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <Box marginTop="Sm">
                    {t("Common.genericErrorMessage")}
                    {t("Common.genericContactSupport")}
                </Box>
            </AlertModal>
        </>
    );
}
